import { useEffect, useMemo, useState } from "react";
import { X, Ruler, ArrowRight } from "lucide-react";
import { cmToInput, inputToCm, lengthLabel } from "@/lib/units";

const STORAGE_KEY = "mp_fit_profile_v1";

/**
 * FitFinderDialog — visitor-side "will this fit me?" widget.
 *
 * Compares the visitor's stored body measurements against the piece's typed
 * measurements_map. Only renders rows for fields the piece actually publishes
 * (so a Dress shows bust/waist/hip/length; a Bottom shows waist/hip/inseam/rise).
 *
 * Visitor measurements persist to localStorage (per device, no account needed).
 *
 * Props:
 *  - open: bool
 *  - onClose()
 *  - item: storefront item with category_schema + measurements_map + category_name
 *  - unitSystem: "metric" | "imperial"
 */
export default function FitFinderDialog({ open, onClose, item, unitSystem }) {
  // Visitor's body measurements — stored as cm in localStorage, mirroring item storage.
  const [profile, setProfile] = useState({});

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setProfile(JSON.parse(raw) || {});
    } catch (e) { /* noop — private mode etc. */ }
  }, [open]);

  // Length-only rows from the piece's schema that actually have a value.
  const rows = useMemo(() => {
    if (!item) return [];
    const schema = item.category_schema || [];
    const map = item.measurements_map || {};
    return schema
      .filter((f) => f.unit_kind === "length" && map[f.key] !== undefined && map[f.key] !== null && map[f.key] !== "")
      .map((f) => ({
        key: f.key,
        label: f.label,
        itemCm: Number(map[f.key]),
        youCm: profile[f.key] !== undefined ? Number(profile[f.key]) : null,
      }));
  }, [item, profile]);

  if (!open) return null;

  const updateMine = (key, raw) => {
    // Empty input → drop the key from the profile rather than storing NaN.
    let next;
    if (raw === "" || raw === null || raw === undefined) {
      next = { ...profile };
      delete next[key];
    } else {
      next = { ...profile, [key]: inputToCm(raw, unitSystem) };
    }
    setProfile(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) { /* noop */ }
  };

  const unit = lengthLabel(unitSystem);
  const hasAnyMine = rows.some((r) => r.youCm !== null && !isNaN(r.youCm) && r.youCm > 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-auto"
      onClick={onClose}
      data-testid="fit-finder-dialog"
    >
      <div
        className="bg-ivory border border-taupe max-w-lg w-full p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} aria-label="close" className="absolute top-4 right-4 text-taupe hover:text-charcoal" data-testid="fit-finder-close">
          <X className="w-5 h-5" />
        </button>

        <div className="mb-2 flex items-center gap-2">
          <Ruler className="w-4 h-4 text-brass" />
          <span className="editorial-label">fit finder</span>
        </div>
        <h2 className="font-serif text-3xl lowercase text-charcoal mt-1">will this fit me?</h2>
        <p className="text-xs text-espresso mt-2">enter the measurements you already know — we&apos;ll show you the difference, side by side. stored on this device only.</p>

        {rows.length === 0 ? (
          <p className="text-sm italic text-taupe mt-8" data-testid="fit-finder-empty">
            this piece doesn&apos;t publish typed measurements. enquire to acquire and we&apos;ll measure it for you.
          </p>
        ) : (
          <>
            <div className="mt-8 border-t border-taupe/40 divide-y divide-taupe/30">
              <div className="grid grid-cols-4 gap-2 py-2">
                <span className="text-[0.6rem] tracking-widest uppercase text-taupe">measure</span>
                <span className="text-[0.6rem] tracking-widest uppercase text-taupe text-right">piece</span>
                <span className="text-[0.6rem] tracking-widest uppercase text-taupe text-right">you</span>
                <span className="text-[0.6rem] tracking-widest uppercase text-taupe text-right">Δ</span>
              </div>
              {rows.map((r) => (
                <FitRow key={r.key} row={r} unitSystem={unitSystem} unit={unit} onChangeMine={(raw) => updateMine(r.key, raw)} />
              ))}
            </div>

            {hasAnyMine && (
              <p className="text-[0.6rem] tracking-widest uppercase text-brass mt-6" data-testid="fit-finder-summary">
                Δ positive · piece is roomier · negative · piece is snugger.
              </p>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                data-testid="fit-finder-done"
                className="inline-flex items-center gap-2 bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase"
              >
                done <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FitRow({ row, unitSystem, unit, onChangeMine }) {
  const itemDisplay = cmToInput(row.itemCm, unitSystem);
  const youDisplay = row.youCm !== null ? cmToInput(row.youCm, unitSystem) : "";

  let delta = null;
  let deltaClass = "text-taupe";
  let deltaLabel = "—";
  if (row.youCm !== null && !isNaN(row.youCm) && row.youCm > 0) {
    const diffCm = row.itemCm - row.youCm;
    const diffDisplay = cmToInput(Math.abs(diffCm), unitSystem);
    delta = diffCm;
    if (Math.abs(diffCm) < 0.5) {
      deltaClass = "text-brass font-medium";
      deltaLabel = `≈ 0 ${unit}`;
    } else if (diffCm > 0) {
      deltaClass = "text-charcoal";
      deltaLabel = `+${diffDisplay} ${unit}`;
    } else {
      deltaClass = "text-espresso";
      deltaLabel = `−${diffDisplay} ${unit}`;
    }
  }

  return (
    <div className="grid grid-cols-4 gap-2 py-3 items-center" data-testid={`fit-row-${row.key}`}>
      <span className="text-sm lowercase text-charcoal">{row.label}</span>
      <span className="text-sm text-charcoal text-right tabular-nums" data-testid={`fit-piece-${row.key}`}>
        {itemDisplay} <span className="text-[0.6rem] text-taupe">{unit}</span>
      </span>
      <input
        type="number"
        step="0.5"
        min="0"
        value={youDisplay}
        onChange={(e) => onChangeMine(e.target.value)}
        onFocus={(e) => e.target.select()}
        placeholder="—"
        data-testid={`fit-you-${row.key}`}
        className="w-full bg-transparent border-b border-taupe py-1 text-right text-sm text-charcoal focus:outline-none focus:border-brass"
      />
      <span className={`text-sm text-right tabular-nums ${deltaClass}`} data-testid={`fit-delta-${row.key}`}>
        {deltaLabel}
      </span>
    </div>
  );
}
